import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useInfiniteQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useShop } from '@/state';
import { track } from '@/lib/analytics';
import { pluralize } from '@alkor/utils';
import { IconChevron, IconDot, IconSearch, IconSliders } from '@/components/icons';
import { ProductTile, TileSkeleton, EmptyState, ErrorState } from '@/components/ui';
import { isNetworkError, isPaused } from '@/lib/online';
import {
  FilterSheet, countFilters, filtersFromSearchParams, filtersToSearchParams, toQuery,
  type FilterDraft,
} from '@/components/FilterSheet';

const SORTS = [
  { value: 'popular', label: 'Популярные' },
  { value: 'price_asc', label: 'Цена ↑' },
  { value: 'price_desc', label: 'Цена ↓' },
  { value: 'new', label: 'Новинки' },
  { value: 'available', label: 'Сначала в наличии' },
] as const;

const PAGE = 20;

/**
 * Список товаров раздела, он же витрина по метке.
 * С badge экран показывает не категорию, а подборку («Акции»): фильтры,
 * сортировка и подгрузка те же, поэтому отдельный экран не нужен.
 */
export function CategoryScreen({ badge, title: fixedTitle }: { badge?: string; title?: string } = {}) {
  const { slug = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const { inCart, addToCart, subscribeStock, busy } = useShop();

  const [sortOpen, setSortOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);

  const sort = params.get('sort') ?? 'popular';
  // Фильтры живут в URL: ссылка на отфильтрованный список работает и пересылается
  const filters = useMemo(() => filtersFromSearchParams(params), [params]);
  const activeCount = countFilters(filters);

  const applyFilters = (next: FilterDraft) => {
    setParams(filtersToSearchParams(next, sort), { replace: true });
    setSheetOpen(false);
  };

  const setSort = (value: string) => {
    setParams(filtersToSearchParams(filters, value), { replace: true });
    setSortOpen(false);
  };

  const listQuery = useInfiniteQuery({
    queryKey: ['products', { slug, badge, sort, filters }],
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      api.products({
        ...(badge ? { badge } : { category: slug }),
        sort, page: pageParam as number, limit: PAGE, ...toQuery(filters),
      }),
    getNextPageParam: (last, all) => (last.meta?.hasMore ? all.length + 1 : undefined),
    staleTime: 60_000,
  });

  const items = useMemo(() => listQuery.data?.pages.flatMap((p) => p.items) ?? [], [listQuery.data]);
  const total = listQuery.data?.pages[0]?.meta?.total ?? 0;

  useEffect(() => { track('category_view', { props: { slug: badge ?? slug } }); }, [slug, badge]);

  const title = fixedTitle ?? items[0]?.categoryName ?? 'Каталог';

  return (
    <>
      <div className="screen-title">{title}</div>
      <div className="screen-sub">
        {listQuery.isLoading
          ? 'Загружаем…'
          : activeCount > 0
            ? `${pluralize(total, 'товар', 'товара', 'товаров')} по фильтру`
            : pluralize(total, 'товар', 'товара', 'товаров')}
      </div>

      <div className="filters">
        <button
          type="button"
          className={`fchip${activeCount > 0 ? ' fchip--on' : ''}`}
          onClick={() => setSheetOpen(true)}
        >
          <IconSliders size={14} />
          Фильтры
          {activeCount > 0 && <span className="fchip__badge">{activeCount}</span>}
        </button>

        <button
          type="button"
          className={`fchip${filters.available ? ' fchip--on' : ''}`}
          onClick={() => applyFilters({ ...filters, available: !filters.available })}
        >
          <IconDot size={14} />
          В наличии
        </button>

        <button type="button" className="fchip" onClick={() => setSortOpen((v) => !v)}>
          {SORTS.find((s) => s.value === sort)?.label}
          <IconChevron size={14} />
        </button>
      </div>

      {sortOpen && (
        <div className="filters" role="radiogroup" aria-label="Сортировка">
          {SORTS.map((s) => (
            <button
              key={s.value}
              type="button"
              role="radio"
              aria-checked={sort === s.value}
              className={`fchip${sort === s.value ? ' fchip--on' : ''}`}
              onClick={() => setSort(s.value)}
            >
              {s.label}
            </button>
          ))}
        </div>
      )}

      <div className="block" style={{ marginTop: 16 }}>
        {/* Без связи запрос стоит на паузе: ошибки не будет, поэтому
            состояние «нет связи» показываем отдельно от скелетона */}
        {isPaused(listQuery) && <ErrorState offline onRetry={() => void listQuery.refetch()} />}

        {/* isPending, а не isLoading: между повторными попытками запрос
            не «фетчится», и на паузе экран показывал бы «ничего не найдено» */}
        {listQuery.isPending && !listQuery.isError && !isPaused(listQuery) && <TileSkeleton count={5} />}

        {listQuery.isError && (
          <ErrorState
            message={(listQuery.error as Error).message}
            offline={isNetworkError(listQuery.error)}
            onRetry={() => void listQuery.refetch()}
          />
        )}

        {listQuery.isSuccess && items.length === 0 && (
          <EmptyState
            icon={<IconSearch size={24} />}
            title="Под фильтр ничего не подошло"
            text="Попробуйте снять часть ограничений — часть позиций доступна под заказ."
            action={
              <button
                type="button"
                className="secbtn"
                onClick={() => setParams(new URLSearchParams(), { replace: true })}
              >
                Сбросить фильтры
              </button>
            }
          />
        )}

        {items.length > 0 && (
          <div className="plist">
            {items.map((p) => (
              <ProductTile
                key={p.id}
                product={p}
                inCart={inCart(p.id)}
                busy={busy}
                onAdd={(x) => void addToCart(x)}
                onNotify={(x) => void subscribeStock(x)}
              />
            ))}
          </div>
        )}

        {listQuery.hasNextPage && (
          <button
            type="button"
            className="secbtn"
            style={{ marginTop: 12 }}
            disabled={listQuery.isFetchingNextPage}
            onClick={() => void listQuery.fetchNextPage()}
          >
            {listQuery.isFetchingNextPage ? 'Загружаем…' : 'Показать ещё'}
          </button>
        )}
      </div>
      <div className="pad" />

      {sheetOpen && (
        <FilterSheet
          categorySlug={slug}
          value={filters}
          onApply={applyFilters}
          onClose={() => setSheetOpen(false)}
        />
      )}
    </>
  );
}
